import React, { useState, useEffect } from "react";
import { useLocation, Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import MarketingBackdrop from "../components/MarketingBackdrop";
import SEO, { SITE_URL } from "../components/SEO";
import api from "../api/axios";

const BlogDetail = () => {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const initialBlog = location.state?.blogData || null;
  const [blog, setBlog] = useState(initialBlog);
  const [loading, setLoading] = useState(!initialBlog);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!initialBlog && slug) {
      const fetchBlog = async () => {
        try {
          const response = await api.get(`/api/blogs/${slug}/`);
          setBlog(response.data);
        } catch (err) {
          console.error("Failed to fetch blog:", err);
          setError(true);
        } finally {
          setLoading(false);
        }
      };
      fetchBlog();
    }
  }, [slug, initialBlog]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-16 h-16 relative">
          <div className="absolute inset-0 border-4 border-blue-500/20 rounded-full"></div>
          <div className="absolute inset-0 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  // Fallback if accessed via direct URL instead of clicking Link AND fetch failed
  if (error || !blog) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-[#0a0a0a]">
        <h2 className="text-2xl font-bold dark:text-white mb-4">Blog Not Found</h2>
        <button onClick={() => navigate('/blogs')} className="px-6 py-2 bg-blue-600 text-white rounded-xl">Go Back</button>
      </div>
    );
  }

  const handleDownload = () => {
    const element = document.createElement("a");
    const fileContent = `${blog.title}\n\nPublished: ${new Date(blog.published_date).toLocaleDateString()}\nCategory: ${blog.category}\n\n${blog.content}`;
    const file = new Blob([fileContent], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${blog.title.replace(/\s+/g, '_')}.txt`;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: blog.title,
          text: `Check out this daily market briefing from FinAI: ${blog.title}`,
          url: window.location.href,
        });
      } catch (err) {
        console.error("Share failed:", err);
      }
    } else {
      // Fallback: Copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  // Convert basic markdown to formatted HTML paragraphs
  const formattedContent = blog.content.split('\n').map((paragraph, index) => {
    if (!paragraph.trim()) return <br key={index} />;
    
    // Simple bold markdown parsing (**text**)
    const pHTML = paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // If it is a list item
    if (pHTML.startsWith('- ') || pHTML.startsWith('* ')) {
       return <li key={index} className="ml-6 mb-2 list-disc" dangerouslySetInnerHTML={{ __html: pHTML.substring(2) }} />;
    }
    
    return <p key={index} className="mb-4 text-base md:text-lg tracking-wide leading-relaxed" dangerouslySetInnerHTML={{ __html: pHTML }} />;
  });

  // Plain-text excerpt for the meta description (strip markdown, ~155 chars).
  const excerpt = blog.content
    .replace(/[#*_>`-]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 155);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: blog.title,
    image: blog.image_url ? [blog.image_url] : undefined,
    datePublished: blog.published_date,
    dateModified: blog.created_at || blog.published_date,
    articleSection: blog.category,
    author: { "@type": "Organization", name: "FinAI Global" },
    publisher: {
      "@type": "Organization",
      name: "FinAI Global",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/logo512.png` },
    },
    mainEntityOfPage: `${SITE_URL}/blogs/${blog.slug}`,
  };

  return (
    <div className="relative isolate min-h-screen bg-gray-50 dark:bg-[#060B12] transition-colors pb-20">
      <SEO
        path={`/blogs/${blog.slug}`}
        title={blog.title}
        description={excerpt || "Daily AI-generated market briefing from FinAI."}
        type="article"
        image={blog.image_url || `${SITE_URL}/og-image.png`}
        jsonLd={articleJsonLd}
      />
      <MarketingBackdrop />
      <Navbar />

      {/* Hero Image Header */}
      <div className="w-full h-[40vh] md:h-[55vh] relative mt-16">
        <div className="absolute inset-0 bg-gradient-to-t from-gray-50 dark:from-[#0a0a0a] via-gray-900/50 to-transparent z-10"></div>
        {blog.image_url ? (
          <img src={blog.image_url} alt="Blog Header" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-r from-blue-900 to-indigo-900"></div>
        )}
      </div>

      {/* Main Container */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-20 -mt-32">
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-2xl rounded-3xl p-8 md:p-12 shadow-2xl border border-white/50 dark:border-white/10">

          {/* Navigation & Actions */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8 border-b border-gray-200 dark:border-gray-800 pb-6">
            <button 
              onClick={() => navigate('/blogs')}
              className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition font-bold"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
              Back to Briefings
            </button>

            <div className="flex items-center gap-3">
              <button onClick={handleShare} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold transition">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"></path></svg>
                Share
              </button>
              <button onClick={handleDownload} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/60 font-semibold transition border border-blue-200 dark:border-blue-500/30">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
                Download Text
              </button>
            </div>
          </div>

          {/* Metadata */}
          <div className="flex items-center gap-3 mb-6">
            <span className="px-3 py-1 rounded-full text-xs font-extrabold tracking-wider uppercase bg-blue-500 text-white">
              {blog.category}
            </span>
            <span className="text-gray-500 dark:text-gray-400 font-medium text-sm">
              {new Date(blog.published_date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          </div>

          {/* Blog Title */}
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-8">
            {blog.title}
          </h1>

          {/* AI Banner */}
          <div className="flex items-center gap-3 p-4 mb-10 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 border border-blue-100 dark:border-blue-800/30">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-500 text-white">
              ✨
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900 dark:text-blue-100">AI-Synthesized Briefing</p>
              <p className="text-xs text-blue-600 dark:text-blue-300/70">Generated autonomously from top trending market indicators.</p>
            </div>
          </div>

          {/* Blog Content */}
          <div className="prose prose-lg dark:prose-invert max-w-none text-gray-800 dark:text-gray-200">
            {formattedContent}
          </div>

        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
